/* =========================================================================
 * ARE WE READY TO CROWN THIS ONE? — preparation, scored, before the win.
 *
 * Brian: "now you understand the challenge of crowning a champion but doing so
 * with preparation. its the reason this has to be done."
 *
 * ⚠️ THIS IS WHAT ALL OF IT WAS FOR, AND IT REFRAMES EVERY PIECE.
 *
 * Crowning a champion after the fact is not the job. By the time a title is
 * announced the school is already being called, and the ring goes to whoever
 * was READY — who already had the crest, the colours, the rep and the artwork
 * standing by. A championship record that is merely CORRECT is worth very
 * little; a championship record that is correct AND PREPARED is the order.
 *
 * So every piece of the last few days resolves into one question per row:
 *
 *   THE DATE          → is it even on the calendar? (2,580 records were not)
 *   THE FIELD         → who can actually win it? (school × sport → conference)
 *   THE CREST         → do we own their mark, hosted, ready to render?
 *   THE COLOURS       → verified brand colours, or a guess at proof time?
 *   THE OWNER         → is a rep on it, or does it belong to nobody?
 *   IS IT REAL        → a phantom can never be crowned at all
 *
 * A gap in any one of them is not a data-quality complaint. It is the specific
 * reason a real order will be lost on a specific day, and the countdown says
 * how long there is to fix it.
 *
 * ⚠️ AND IT EXPLAINS WHY THE PHANTOMS MATTERED SO MUCH. Preparation is finite —
 * a rep can only ready so many championships before a season. Every hour spent
 * preparing a Big Ten water polo title that does not exist is an hour not spent
 * on one that does. A phantom does not just clutter the board; it consumes the
 * exact resource this model exists to allocate.
 *
 * Pure. No fetch, no React, no writes.
 * ========================================================================= */

import { conferenceFor } from './sportAffiliation.js'

const S = (v) => String(v ?? '').trim()

/* ---- THE CHECKS -------------------------------------------------------
 * Each returns { ok, why, fix } — and `fix` is the point. "No crest" is a
 * complaint; "no crest — run the Marks Library finder" is a next action, which
 * is the difference between a report and a worklist. */

export const CHECKS = [
  {
    id: 'real',
    label: 'It exists',
    /* ⚠️ FIRST, BECAUSE NOTHING ELSE MATTERS IF IT IS FALSE. A retired phantom
     * can never be crowned, so preparing it is pure waste. */
    weight: 0,
    test: (c) => !/^RETIRED - /.test(S(c?.name ?? c?.raw?.championship_name)),
    why: 'Retired — this championship does not exist and can never be crowned.',
    fix: 'Nothing to prepare. It is kept only for the audit trail.',
  },
  {
    id: 'date',
    label: 'On the calendar',
    weight: 3,
    test: (c) => Boolean(c?.effDate || S(c?.raw?.sr_event_date)),
    why: 'No event date, so it never appears in Stages, Worklist or the Calendar.',
    fix: 'Read the governing body’s published championship schedule.',
  },
  {
    id: 'field',
    label: 'We know who can win',
    weight: 3,
    /* The association is what turns a title into a list of named prospects. */
    test: (c) => {
      const conf = S(c?.conference ?? c?.raw?.sr_conference)
      return Boolean(conf)
    },
    why: 'No governing body on the record, so there is no way to work out who competes for it.',
    fix: 'Set the conference or association, then source its member schools by sport.',
  },
  {
    id: 'sport',
    label: 'Sport is set',
    weight: 2,
    test: (c) => Boolean(S(c?.sport ?? c?.raw?.sr_sport)),
    why: 'No sport, so it is invisible to every sport filter and cannot be matched to a school’s programme.',
    fix: 'Set the sport from the championship name.',
  },
  {
    id: 'owner',
    label: 'A rep is assigned',
    weight: 2,
    test: (c) => {
      const o = S(c?.rep ?? c?.owner ?? c?.raw?.sr_assigned_rep)
      return Boolean(o) && !/^(unassigned|unknown|none|n\/a)$/i.test(o)
    },
    why: 'No rep is assigned, so nobody is preparing it and nobody will call on the day.',
    fix: 'Assign from the Assignments worklist — the engine proposes from win history.',
  },
  {
    id: 'title',
    label: 'Which title it is',
    weight: 1,
    /* Only meaningful for a conference record, which crowns two champions. */
    test: (c) => {
      const lvl = S(c?.raw?.sr_level ?? c?.level)
      const seg = S(c?.raw?.sr_segment ?? c?.segment)
      if (lvl !== 'Conference' && seg !== 'Conference') return true
      return /regular[\s-]?season|tournament|championship game/i.test(S(c?.name ?? c?.raw?.championship_name))
    },
    why: 'A conference crowns a regular-season AND a tournament champion — usually different schools — and this record does not say which it tracks.',
    fix: 'Confirm against the conference’s own site, then rename.',
  },
]

/**
 * Score one championship's preparedness.
 *
 * ⚠️ A RETIRED RECORD SCORES `null`, NOT ZERO. Zero would sort it to the top of
 * a worklist ordered by "least ready" — putting the one thing that can never be
 * crowned at the front of the queue. Not-applicable and not-ready are different
 * states and the difference is the whole usefulness of the ranking.
 */
export function readinessOf(champ) {
  const real = CHECKS[0].test(champ)
  if (!real) return { applicable: false, score: null, missing: [], ready: false }

  const scored = CHECKS.filter((k) => k.weight > 0)
  const missing = []
  let got = 0
  let total = 0
  for (const k of scored) {
    total += k.weight
    if (k.test(champ)) got += k.weight
    else missing.push({ id: k.id, label: k.label, why: k.why, fix: k.fix })
  }
  return {
    applicable: true,
    score: total ? Math.round((got / total) * 100) : 0,
    missing,
    ready: missing.length === 0,
  }
}

/**
 * ⚠️ URGENCY IS READINESS × TIME, NOT EITHER ALONE.
 *
 * A championship 400 days out with nothing prepared is fine — there is a year
 * to fix it. One in nine days with no owner and no crest is an order about to
 * be lost, and it should outrank a hundred unprepared records in a distant
 * season. Sorting a worklist by readiness alone buries exactly the rows that
 * still have time to be saved.
 */
export function urgencyOf(champ, now = new Date()) {
  const r = readinessOf(champ)
  if (!r.applicable) return { ...r, days: null, urgency: 0, band: 'retired' }

  const d = champ?.effDate ? new Date(champ.effDate) : null
  const days = d && !Number.isNaN(d.getTime())
    ? Math.round((d.getTime() - now.getTime()) / 86400000)
    : null

  /* No date is its own kind of urgent: it cannot be prepared for a day nobody
   * knows. Deliberately ranked above a distant championship rather than last. */
  if (days === null) return { ...r, days: null, urgency: r.ready ? 0 : 60, band: 'no-date' }
  if (days < 0) return { ...r, days, urgency: 0, band: 'past' }
  if (r.ready) return { ...r, days, urgency: 0, band: 'ready' }

  /* Closer = more urgent, scaled by how much is missing. 90 days is the horizon
   * at which preparation actually has to start — the same window the Command
   * Center's lens already uses, so the two agree about what "soon" means. */
  const timePressure = Math.max(0, Math.min(1, (90 - days) / 90))
  const gap = (100 - r.score) / 100
  const urgency = Math.round(timePressure * gap * 100)
  return {
    ...r,
    days,
    urgency,
    band: days <= 30 ? 'critical' : days <= 90 ? 'soon' : 'horizon',
  }
}

/**
 * The preparation worklist: what to fix, in the order it will cost us.
 *
 * Retired records are dropped entirely — not scored low, DROPPED. Preparation
 * is a finite resource and a phantom consumes it for nothing.
 */
export function prepWorklist(records = [], { now = new Date(), limit = 200 } = {}) {
  const rows = []
  for (const c of records) {
    const u = urgencyOf(c, now)
    if (!u.applicable || u.ready || u.band === 'past') continue
    rows.push({ champ: c, ...u })
  }
  rows.sort((a, b) => b.urgency - a.urgency || (a.days ?? 9e9) - (b.days ?? 9e9))
  return { rows: rows.slice(0, limit), total: rows.length }
}

/** How prepared is the whole board? Reported as the gap, not the coverage. */
export function readinessSummary(records = [], now = new Date()) {
  let applicable = 0, ready = 0, critical = 0, retired = 0
  const byGap = {}
  for (const c of records) {
    const u = urgencyOf(c, now)
    if (!u.applicable) { retired += 1; continue }
    applicable += 1
    if (u.ready) { ready += 1; continue }
    if (u.band === 'critical') critical += 1
    for (const m of u.missing) byGap[m.id] = (byGap[m.id] || 0) + 1
  }
  return {
    applicable,
    ready,
    notReady: applicable - ready,
    /* The number that actually matters: unprepared AND inside 30 days. */
    critical,
    retired,
    byGap,
  }
}

/** Named prospects for a championship — who we would be preparing FOR. */
export function contendersFor(champ, schools = []) {
  const conf = S(champ?.conference ?? champ?.raw?.sr_conference)
  const sport = S(champ?.sport ?? champ?.raw?.sr_sport)
  if (!conf) return []
  return schools.filter((s) => {
    const c = conferenceFor(s?.name ?? s, sport)
    return c && c.toLowerCase() === conf.toLowerCase()
  })
}
